import type { FC } from 'react'
import { ChevronRight, TrendingUp, Clock, AlertCircle, UserX, ArrowUpRight } from 'lucide-react'
import type { InsightItem } from '../core/dashboard-types'
import CardInfoButton from './CardInfoButton'

const insightsMeta = {
  description: 'Automated fleet-level observations and anomalies detected from operational data across vessels.',
  endpoint: 'GET /qhse/defects + GET /pms/calendar-events + GET /operations/voyages',
  logic: 'Cross-references open defects, overdue PMS jobs, and active voyages to surface anomalies: overdue audits, unresolved defects >30 days, and active voyage counts.',
}

type Props = {
  insights: InsightItem[]
  onViewAll?: () => void
  onInsightClick?: (insight: InsightItem) => void
}

const colorMap: Record<InsightItem['severity'], { bg: string; border: string; iconBg: string; iconText: string; text: string; highlight: string }> = {
  amber: { bg: 'bg-amber-50/50', border: 'border-amber-100/50', iconBg: 'bg-amber-100', iconText: 'text-amber-600', text: 'text-slate-700', highlight: 'text-amber-700 font-semibold' },
  red: { bg: 'bg-red-50/50', border: 'border-red-100/50', iconBg: 'bg-red-100', iconText: 'text-red-600', text: 'text-slate-700', highlight: 'text-red-700 font-semibold' },
  orange: { bg: 'bg-orange-50/50', border: 'border-orange-100/50', iconBg: 'bg-orange-100', iconText: 'text-orange-600', text: 'text-slate-700', highlight: 'text-orange-700 font-semibold' },
  blue: { bg: 'bg-blue-50/50', border: 'border-blue-100/50', iconBg: 'bg-blue-100', iconText: 'text-blue-600', text: 'text-slate-700', highlight: 'text-blue-700 font-semibold' },
}

const iconMap: Record<string, typeof TrendingUp> = {
  'trending-up': TrendingUp,
  clock: Clock,
  'alert-circle': AlertCircle,
  'user-x': UserX,
}

const InsightsPanel: FC<Props> = ({ insights, onViewAll, onInsightClick }) => {
  return (
    <div className='card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex-1'>
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-bold text-slate-900'>Insights</h3>
          <CardInfoButton meta={insightsMeta} />
        </div>
        <button className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5' onClick={onViewAll}>
          View all <ChevronRight className='w-3 h-3' />
        </button>
      </div>
      <div className='space-y-3'>
        {insights.length > 0 ? insights.map((item) => {
          const Icon = iconMap[item.icon] || TrendingUp
          const colors = colorMap[item.severity]

          return (
            <div
              key={item.id}
              className={`flex items-start gap-3 p-2.5 rounded-xl ${colors.bg} ${colors.border} border hover:${colors.bg.replace('/50', '')} transition-colors cursor-pointer`}
              onClick={() => onInsightClick?.(item)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onInsightClick?.(item) }}
            >
              <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${colors.iconText}`} />
              </div>
              <div className='flex-1 min-w-0'>
                <p className={`text-xs ${colors.text} leading-relaxed`}>
                  {item.message && <span>{item.message}</span>}
                  <span className={colors.highlight}>{item.highlight}</span>
                </p>
              </div>
              <ArrowUpRight className={`w-4 h-4 ${colors.iconText} flex-shrink-0 mt-1`} />
            </div>
          )
        }) : (
          <div className='py-10 text-center text-slate-400 text-xs'>No insights available</div>
        )}
      </div>
    </div>
  )
}

export default InsightsPanel
